import { useState } from 'react';
import SafeIcon from './SafeIcon';
import { useApp } from '../i18n/AppContext';
import { useUi } from '../store/ui';
import { postStatusHoldsDailySlot, useDemo, useWorkspaceData } from '../store/DemoStore';
import { DEMO_TODAY } from '../store/seed';
import type { Post } from '../types';

const SELECTABLE_DAY_COUNT = 21;
const suggestedTimes = [
  '08:30',
  '09:00',
  '10:00',
  '11:00',
  '12:00',
  '13:30',
  '15:00',
  '17:15',
  '19:00',
];

export default function RescheduleSheet({
  post,
  onClose,
  initialDay,
  onMoved,
}: {
  post: Post;
  onClose: () => void;
  initialDay?: number;
  onMoved?: (from: { day: number; time: string }) => void;
}) {
  const { translate, language, readOnly } = useApp();
  const { route, actor, notify, role } = useUi();
  const isAgencyStaff = role === 'agency';
  const [note, setNote] = useState('');
  const { commands } = useDemo();
  const { posts } = useWorkspaceData(route.workspaceId);
  const [day, setDay] = useState<number | undefined>(initialDay);
  const [time, setTime] = useState(post.time);
  const shortDateFormatter = new Intl.DateTimeFormat(language, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
  const formatDay = (dayOfMonth: number) =>
    shortDateFormatter.format(new Date(2026, 10, dayOfMonth));
  const selectableDays = Array.from(
    { length: SELECTABLE_DAY_COUNT },
    (_, offset) => DEMO_TODAY + 1 + offset
  );
  const findPostTakingDay = (dayOfMonth: number) =>
    posts.find(
      (otherPost) =>
        otherPost.id !== post.id &&
        otherPost.channel === post.channel &&
        otherPost.day === dayOfMonth &&
        postStatusHoldsDailySlot(otherPost.status)
    );
  const timeOptions = [...new Set([post.time, ...suggestedTimes])].sort();
  const selectionIsUnchanged = day === post.day && time === post.time;
  const isApproved = post.status === 'Scheduled' || post.status === 'Approved · Stopped';

  function moveFailureMessage(result: string) {
    if (result === 'taken') return translate('That slot was just taken. Choose another day.');
    if (result === 'past') return translate('Choose a day after today.');
    return translate('This post can no longer be moved.');
  }

  function infoBannerMessage() {
    if (!isAgencyStaff)
      return translate(
        'Your agency moves posts. They get your suggestion and confirm it; the post keeps its current time until then.'
      );
    if (!isApproved) return translate('Only the time changes. The post still needs your review.');
    return translate(
      post.status === 'Approved · Stopped'
        ? 'The approval is kept, and the post is scheduled again at the new time.'
        : 'The approval is kept. Only the time changes.'
    );
  }

  function moveOrSuggest() {
    if (!day) return;
    if (!isAgencyStaff) {
      commands.suggestTime(post.id, day, time, note.trim(), actor);
      notify(
        translate(
          'Suggestion sent. Hakusana confirms the new time; until then the post keeps its current time.'
        )
      );
      onClose();
      return;
    }
    const previousSlot = { day: post.day, time: post.time };
    const moveResult = commands.movePost(post.id, day, time, actor);
    if (moveResult === 'ok') {
      notify(translate('Moved to {when} at {time}.', { when: formatDay(day), time }));
      onMoved?.(previousSlot);
      onClose();
    } else notify(moveFailureMessage(moveResult), 'warn');
  }

  return (
    <div
      className="drawer-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="drawer reschedule-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="move-title"
      >
        <div className="drawer-header">
          <div>
            <h2 id="move-title">
              {translate(isAgencyStaff ? 'Move “{title}”' : 'Suggest a new time for “{title}”', {
                title: post.title,
              })}
            </h2>
            <div className="muted">
              {post.channel} ·{' '}
              {translate('now {when} at {time}', {
                when: formatDay(post.day),
                time: post.time,
              })}
            </div>
          </div>
          <button className="icon-button" onClick={onClose} aria-label={translate('Close')}>
            <SafeIcon name="FiX" />
          </button>
        </div>
        <p className="muted small-print">
          {translate('One post per channel per day. Days with a post on {channel} are taken.', {
            channel: post.channel,
          })}
        </p>
        <div className="slot-picker" role="radiogroup" aria-label={translate('Choose a day')}>
          {selectableDays.map((dayOfMonth) => {
            const postTakingDay = findPostTakingDay(dayOfMonth);
            const isCurrentDay = dayOfMonth === post.day;
            const isSelected = day === dayOfMonth;
            const dayStatusLabel = isCurrentDay
              ? translate('Current day · change the time')
              : postTakingDay
                ? postTakingDay.title
                : translate('Free');
            return (
              <button
                key={dayOfMonth}
                type="button"
                role="radio"
                aria-checked={isSelected}
                className={`slot-option ${isSelected ? 'selected' : ''} ${postTakingDay ? 'taken' : ''} ${isCurrentDay ? 'current' : ''}`}
                disabled={!!postTakingDay}
                onClick={() => setDay(dayOfMonth)}
                title={
                  postTakingDay
                    ? translate('Taken by “{title}”', { title: postTakingDay.title })
                    : undefined
                }
              >
                <strong>{formatDay(dayOfMonth)}</strong>
                <span className="muted">{dayStatusLabel}</span>
              </button>
            );
          })}
        </div>
        <label className="wizard-field">
          {translate('Time')}
          <select
            className="select-field"
            value={time}
            onChange={(event) => setTime(event.target.value)}
          >
            {timeOptions.map((timeOption) => (
              <option key={timeOption}>{timeOption}</option>
            ))}
          </select>
        </label>
        {!isAgencyStaff && (
          <label className="wizard-field">
            {translate('Note for Hakusana (optional)')}
            <input
              className="text-field"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder={translate('For example: the tasting moved to Friday')}
            />
          </label>
        )}
        <div className="notice-banner">
          <SafeIcon name="FiInfo" />
          <span>{infoBannerMessage()}</span>
        </div>
        <div className="picker-footer">
          <button className="button" onClick={onClose}>
            {translate('Cancel')}
          </button>
          <button
            className="button primary"
            disabled={readOnly || !day || selectionIsUnchanged}
            onClick={moveOrSuggest}
          >
            <SafeIcon name="FiCalendar" />
            {day
              ? translate(isAgencyStaff ? 'Move to {when}' : 'Suggest {when}', {
                  when: formatDay(day),
                })
              : translate('Choose a day')}
          </button>
        </div>
      </section>
    </div>
  );
}
